import { Adsense } from "@ctrl/react-adsense";
import { useEffect, useRef, useState } from "react";

interface AdSlotProps {
  client: string;
  slot: string;
  format?: string;
  layout?: string;
  layoutKey?: string;
  responsive?: string;
  height?: number;
  marginBottom?: number;
  className?: string;
}

/**
 * Wraps @ctrl/react-adsense's <Adsense/> so an ad slot that never gets
 * filled (blocked, unapproved domain, localhost/dev, no inventory for this
 * user) doesn't sit there as permanent dead space on the page.
 *
 * AdSense marks the underlying <ins> with data-ad-status="filled" or
 * "unfilled" once it decides what to do with the slot. We watch for that
 * attribute and collapse the reserved height/margin the moment we know the
 * slot came back empty, instead of holding the layout open for an ad that
 * was never going to show. If AdSense's script never touches the slot at
 * all (no network, script blocked, dev environment), a short timeout
 * collapses it too.
 */
const AdSlot = ({
  client,
  slot,
  format = "auto",
  layout = "",
  layoutKey = "",
  responsive = "true",
  height = 90,
  marginBottom = 30,
  className = "",
}: AdSlotProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const ins = wrapper?.querySelector<HTMLElement>("ins.adsbygoogle");
    if (!ins) return;

    const checkStatus = () => {
      const status = ins.getAttribute("data-ad-status");
      if (status === "unfilled") {
        setCollapsed(true);
      } else if (status === "filled") {
        setCollapsed(false);
      }
    };

    checkStatus();

    const observer = new MutationObserver(checkStatus);
    observer.observe(ins, {
      attributes: true,
      attributeFilter: ["data-ad-status"],
    });

    // AdSense never even attempted this slot (blocked script, no ad
    // network reachable, unapproved/dev domain) — stop reserving space.
    const timeout = setTimeout(() => {
      if (!ins.getAttribute("data-ad-status")) {
        setCollapsed(true);
      }
    }, 4000);

    return () => {
      observer.disconnect();
      clearTimeout(timeout);
    };
  }, [slot]);

  return (
    <div
      ref={wrapperRef}
      className={className}
      style={{
        width: "100%",
        overflow: "hidden",
        height: collapsed ? 0 : height,
        marginBottom: collapsed ? 0 : marginBottom,
        transition: "height 300ms ease, margin 300ms ease",
      }}
    >
      <Adsense
        client={client}
        slot={slot}
        format={format}
        layout={layout}
        layoutKey={layoutKey}
        responsive={responsive}
        style={{
          display: "flex",
          width: "100%",
          height: `${height}px`,
          justifyContent: "center",
        }}
      />
    </div>
  );
};

export default AdSlot;
